/**
 * The shell domain's face (ADR 0014, Rule D).
 *
 * What wraps the app: the provider stack, the two error boundaries and their screens, the landing
 * page, and the `/app` composition. Every file outside `src/shell/` reaches this domain through
 * this module; nothing below it is a path another domain may name.
 *
 * **Explicit named exports, never `export *`.** A face is an interface, not a bag.
 *
 * **Two faces, and the other one is `observability/index.ts`.** `sentry-local.ts` imports
 * `better-sqlite3`, `node:fs` and `node:path`, so publishing it here would put a native addon into
 * the client graph of every component this face carries. `account/`, `enrichment/` and `social/`
 * each answered the same problem the same way; the name follows `docs/target-structure.md`'s tree
 * rather than inventing a `shell/server/`.
 *
 * **Every consumer of this face is a route file**, which is the shape ADR 0010 predicts: a file
 * Next.js resolves by name keeps its export and delegates. `app/layout.tsx` takes `RootProviders`,
 * `app/app/layout.tsx` takes `ProtectedLayout`, `app/page.tsx` takes `LandingPage`, and
 * `app/error.tsx` and `app/global-error.tsx` take a screen each. Five files, five names — the face
 * is exactly as wide as the route tier it serves.
 *
 * **What this domain does not own.** Navigation split out under #152 and is `@/navigation`; the
 * other three providers are their own domains' — `EntryUpdateProvider` is `capture/`'s,
 * `InboxProvider` is `social/`'s, `EmbeddingProvider` is `search/`'s, and `AutomergeProvider` is
 * `document/`'s. "Welcome back" is `resurfacing/`'s against the placement table, for the reason
 * that face records.
 *
 * **Two files did not come with the move.** `components/error-display.tsx` (103 lines) had no
 * importer anywhere and was deleted rather than carried — step 0.2's rule, and #317's own
 * acceptance criterion. `components/layout/right-panel.tsx` (23 lines) was deleted with
 * [#157](https://github.com/cinjoff/konstantout/issues/157): it was mounted by nobody while its
 * provider is mounted for every route, and #157 is about whether that surface ships at all. #314
 * read `topic-filter-panel.tsx` the same way one step earlier. `RightPanelProvider` stays — it is
 * mounted, and `useRightPanelContent` is how a page injects content.
 *
 * @see docs/target-structure.md step 8, ADR 0010, ADR 0014
 */

// ============================================================================
// Composition
// ============================================================================

export { ProtectedLayout } from './protected-layout'
export type { ProtectedLayoutProps } from './protected-layout'

// ============================================================================
// Providers
//
// **`RootProviders` is what crosses, not the providers it mounts.** `app/layout.tsx` held the
// whole tree inline — `ErrorBoundary > AutomergeProvider > Suspense > ReactQueryProvider` plus the
// toaster and the analytics — which made a route file own composition, the one thing ADR 0010 says
// an adapter does not do. Publishing the stack rather than its parts means the order, which is
// load-bearing, is stated in one module that can be read and changed as a unit.
// `ReactQueryProvider` is not published: `RootProviders` is its only mount.
//
// **Nothing from `RightPanelProvider` is published, and measuring is how that was decided.**
// `RightPanelProvider` is mounted by `protected-layout.tsx` inside this domain, and
// `useRightPanel` and `useRightPanelContent` have **zero** callers in `src/` or `e2e/` now that
// `right-panel.tsx` is deleted — its only reader. So the panel's whole machinery is unreachable,
// which is #157's finding stated more sharply than #157 states it. `topics/` left three
// zero-consumer read hooks internal on the same rule one step earlier. A face edge is the one
// thing that would put this back in a build graph, and that is #157's call to make.
// ============================================================================

export { RootProviders } from './root-providers'

// ============================================================================
// Error screens
//
// What Next.js renders when the boundary does not catch. `GlobalErrorScreen` renders its own
// `<html>` and depends on nothing, because it runs when the layout itself has failed.
//
// **`ErrorBoundary` is not published, and that changed with the root provider stack.** Its one
// external caller was `app/layout.tsx`, which now takes `RootProviders` instead; the two remaining
// mounts — `root-providers.tsx` and `protected-layout.tsx` — are both inside this domain and reach
// it relatively. Leaving the edge would be an interface kept for a caller that no longer exists,
// which is the measurement `RightPanelProvider` is held to four lines above.
// ============================================================================

export { ErrorScreen } from './error-screen'
export type { ErrorScreenProps } from './error-screen'
export { GlobalErrorScreen } from './global-error-screen'
export type { GlobalErrorScreenProps } from './global-error-screen'

// ============================================================================
// Landing
//
// `HeroWordRotate` is not published: `landing-page.tsx` is its only consumer, inside the domain.
// ============================================================================

export { LandingPage } from './landing/landing-page'
